import { Reducer, useCallback, useEffect, useReducer } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { SHIFT_CANCELLATION_REASON_TYPES } from "src/constants/shifts";
import { deleteShift, unassignShift } from "src/api/shifts";
import { getErrorMessage } from "src/utils/errors";

import {
  fetchExclusionDetails,
  fetchAgentDetails,
  fetchAssignedShifts,
  fetchAssignedShiftsCount,
} from "./api";
import { initialState, reducer } from "./reducer";

import {
  HTTPError,
  ReducerAction,
  ReducerActions,
  UseWorkplaceWorkerProps,
  UseWorkplaceWorkerReturnType,
  WorkplaceWorkerState,
} from "./workplaceWorker.types";

import { WhoCalledRatingPerformanceModal } from "@src/appV2/components/BlockWorkersAndGetDonuts/constants";
import { createEvent } from "src/api/events";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";
import { getActiveTabName, logViewDocument } from "../logs";
import { WhoCalledChatModal, logChatOpen } from "src/containers/chat/utils/logEvents";
import { showErrorToast, showInfoToast } from "@src/appV2/lib/Notifications";
import { useInvalidateBookingRequests } from "@src/appV2/BookingRequests/api/useInvalidateBookingRequests";

export function useWorkplaceWorker(props: UseWorkplaceWorkerProps): UseWorkplaceWorkerReturnType {
  const { workplaceId, workerId, workplaceUserId } = props;
  const [state, dispatch] = useReducer<Reducer<WorkplaceWorkerState, ReducerActions>>(
    reducer,
    initialState
  );

  const [infiniteScrollRef] = useInfiniteScroll({
    loading: state.isLoading.shiftDetails[state.activeTab],
    hasNextPage: !!state.shiftDetails[state.activeTab].pageToken,
    onLoadMore: useCallback(() => dispatch({ type: ReducerAction.LOAD_MORE_ASSIGNED_SHIFTS }), []),
    delayInMs: 10,
  });

  const openChatModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (workplaceUserId) {
      logChatOpen({
        workerId,
        workplaceId,
        workplaceUserId,
        location: WhoCalledChatModal.WORKER_DETAILS,
        activeTab: getActiveTabName(state.activeTab),
      });
    }
    dispatch({
      type: ReducerAction.SHOW_CHAT_MODAL,
      payload: `${workplaceId}_${workerId}`,
    });
  };

  const openDnrModal = useCallback(() => {
    dispatch({ type: ReducerAction.SHOW_BLOCK_OR_UNBLOCK_MODAL });
  }, [dispatch]);

  const invalidateBookingRequests = useInvalidateBookingRequests();

  const onConfirmCancelShift = async (reasonType?: string, reasonDescription?: string) => {
    const { selectedCancellingShift } = state;
    if (!selectedCancellingShift) {
      return;
    }
    try {
      // When reason selected is `NO_CALL_NO_SHOW` un-assigning the agent from the shift.
      if (reasonType === SHIFT_CANCELLATION_REASON_TYPES.NO_CALL_NO_SHOW) {
        await unassignShift({
          shiftId: selectedCancellingShift._id,
          agentId: selectedCancellingShift.agentId,
          isAgentCancel: false,
          isNoCallNoShow: true,
          userId: workplaceId,
          reasonType,
          reasonDescription,
        });
        showInfoToast("Shift unassigned");
      } else {
        await createEvent(selectedCancellingShift._id, "FACILITY_CANCEL");
        await deleteShift({
          shiftId: selectedCancellingShift._id,
          userId: workplaceId,
          isFacilityCancel: true,
          reasonType,
          reasonDescription,
        });
        showInfoToast("Shift deleted");
      }
      dispatch({
        type: ReducerAction.REMOVE_SHIFT_FROM_VIEW,
        payload: { shiftId: selectedCancellingShift._id },
      });
      logEvent(HCF_USER_EVENTS.CANCEL_SHIFT, {
        shiftId: selectedCancellingShift._id,
        workerId,
        workplaceId,
        workplaceUserId,
        // we should standardize the location
        location: WhoCalledRatingPerformanceModal.WORKER_DETAILS,
        activeTab: state.activeTab,
      });
    } catch (err) {
      showErrorToast(`Shift couldn't be deleted`);
      // eslint-disable-next-line no-console
      console.error("Failed to cancel: " + getErrorMessage(err));
    } finally {
      invalidateBookingRequests();
      dispatch({ type: ReducerAction.HIDE_CANCEL_SHIFT_MODAL });
    }
  };

  const onViewDocument = () => {
    if (workplaceUserId) {
      logViewDocument({
        workerId,
        workplaceId,
        workplaceUserId,
        location: WhoCalledRatingPerformanceModal.WORKER_DETAILS,
        activeTab: getActiveTabName(state.activeTab),
      });
    }
  };

  useEffect(() => {
    if (!workerId || !workplaceId || !state.isLoading.shiftsCount) {
      return;
    }
    (async () => {
      try {
        const payload = await fetchAssignedShiftsCount({
          workerId,
          workplaceId,
        });
        dispatch({ type: ReducerAction.SET_ASSIGNED_SHIFTS_COUNT, payload });
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        showErrorToast("Cannot load count of shifts worked by the worker, please try again later.");
      }
    })();
  }, [workerId, workplaceId, dispatch, state.isLoading.shiftsCount]);

  useEffect(() => {
    if (!workerId || !workplaceId || !state.isLoading.shiftDetails[state.activeTab]) {
      return;
    }
    (async () => {
      try {
        const { activeTab } = state;
        const { pageToken } = state.shiftDetails[state.activeTab];
        const data = await fetchAssignedShifts({
          workerId,
          workplaceId,
          category: activeTab,
          ...(pageToken ? { pageToken } : {}),
        });
        dispatch({
          type: ReducerAction.SET_ASSIGNED_SHIFTS,
          payload: {
            type: activeTab,
            shifts: data.shifts,
            pageToken: data.pageToken,
          },
        });
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        dispatch({ type: ReducerAction.SET_ERROR_ON_ASSIGNED_SHIFTS });
      }
    })();
    // NOTE:: Do not add anymore dependencies here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workerId,
    workplaceId,
    dispatch,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    state.isLoading.shiftDetails[state.activeTab],
    state.activeTab,
  ]);

  useEffect(() => {
    if (!workerId || !workplaceId) {
      return;
    }
    (async () => {
      try {
        dispatch({ type: ReducerAction.SET_LOADING_STATE, payload: { workerDetails: true } });
        const payload = await fetchAgentDetails({ workerId, workplaceId });
        dispatch({ type: ReducerAction.SET_WORKER_DETAILS, payload });
      } catch (err) {
        const error = err as HTTPError;
        dispatch({
          type: ReducerAction.SET_ERROR_ON_WORKER_DETAILS,
          payload: typeof error?.status === "number" ? error.status : true,
        });
      }
    })();
  }, [workerId, dispatch, workplaceId]);

  useEffect(() => {
    if (!workerId || !workplaceId) {
      return;
    }
    (async () => {
      try {
        dispatch({ type: ReducerAction.SET_LOADING_STATE, payload: { exclusion: true } });
        const payload = await fetchExclusionDetails({ workerId, workplaceId });
        dispatch({
          type: ReducerAction.SET_EXCLUSION_DETAILS,
          payload: payload[0],
        });
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        dispatch({ type: ReducerAction.SET_ERROR_ON_EXCLUSION });
        showErrorToast("Error loading DNR for the worker.");
      }
    })();
  }, [workerId, workplaceId, dispatch]);

  return {
    ...state,
    dispatch,
    infiniteScrollRef,
    openDnrModal,
    openChatModal,
    onConfirmCancelShift,
    onViewDocument,
  };
}
